import { lazy } from 'react';
import type { Components } from 'react-markdown';

export interface LazyMarkdownProps {
  children: string;
  /** Element overrides, e.g. an `a` that opens in-app links itself. */
  components?: Components;
  /** URL schemes to keep besides react-markdown's safe defaults, which drop e.g. `help:`. */
  keepSchemes?: string[];
}

/**
 * GitHub-flavoured markdown, with react-markdown + remark-gfm (~157 kB) split into their own
 * chunk and loaded only when markdown first renders — not with the app shell. Render inside
 * a `<Suspense>`.
 */
export const LazyMarkdown = lazy(async () => {
  const [{ default: ReactMarkdown, defaultUrlTransform }, { default: remarkGfm }] = await Promise.all([
    import('react-markdown'),
    import('remark-gfm'),
  ]);
  const Markdown = ({ children, components, keepSchemes = [] }: LazyMarkdownProps) => (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={components}
      urlTransform={(url) => (keepSchemes.some((scheme) => url.startsWith(scheme)) ? url : defaultUrlTransform(url))}
    >
      {children}
    </ReactMarkdown>
  );
  return { default: Markdown };
});
